import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage } from 'react-intl';
import get from 'lodash/get';

import withHostContext, {
  trackingCategories,
  trackingLabels,
  trackingLocations,
  trackClick,
  trackEventGa,
  trackView,
  trackClickTypes,
} from '../../context/host/consumer';
import Icon from '../../components/Icon';
import CommunicateLink from '../shared/CommunicateLink';
import LearnMoreLink from './LearnMoreLink';
import { getUrlWithProtocol } from '../../utils/url';
import SendToAdmissions from '../../components/SendToAdmissions';

import './styles.css';

const messages = defineMessages({
  connect: {
    id: 'hubs.weblinks.connect',
    defaultMessage: 'Connect',
  },
  applyOnline: {
    id: 'hubs.weblinks.apply-online',
    defaultMessage: 'Apply Online',
  },
  virtualTour: {
    id: 'hubs.weblinks.virtual-tour',
    defaultMessage: 'Virtual Tour',
  },
});

export class WebLinks extends React.PureComponent {
  static propTypes = {
    hubsWebLinks: PropTypes.shape({
      premium_links: PropTypes.array,
    }),
    collegeName: PropTypes.string,
    userData: PropTypes.object,
    collegeContact: PropTypes.array,
    admissionsOffice: PropTypes.object,
    sendEmail: PropTypes.func,
    virtualTour: PropTypes.string,
  };

  constructor(props) {
    super(props);

    this.state = {
      showAdmissionsModal: false,
    };
  }

  handleClick = (e) => {
    const productId = e.target.getAttribute('data-click-ppid');
    const location = e.target.getAttribute('data-click-location');
    if (productId && location) {
      trackClick(this.props.hostContext, { productId, location });
    }
    const category = e.target.getAttribute('data-click-category');
    const label = e.target.getAttribute('data-click-label');
    if (category && label) {
      trackEventGa(this.props.hostContext, { type: 'click', category, label });
    }
  };

  handleVirtualTourClick = (e) => {
    trackView(this.props.hostContext, {
      type: trackClickTypes.HUBS_VIEW,
      location: trackingLocations.VIRTUAL_TOUR,
    });
  };

  handleConnectClick = (e) => {
    this.props.hostContext.connectToCollege(e);
  };

  openAdmissionsModal = () => {
    this.setState({
      showAdmissionsModal: true,
    });
  };

  closeAdmissionsModal = () => {
    this.setState({
      showAdmissionsModal: false,
    });
  };

  render() {
    const {
      hubsWebLinks = {},
      collegeName,
      userData,
      collegeContact,
      admissionsOffice,
      sendEmail,
      virtualTour,
    } = this.props;
    const { showAdmissionsModal } = this.state;
    const premiumLinks = [].concat(hubsWebLinks.premium_links || []);
    const applyOnlineLink = premiumLinks.find(
      (link) => link && link.category === 'APPLY_ONLINE',
    );
    const userEmail = get(userData, 'emailAddress', '');
    const isCamino = get(this.props.hostContext, 'isCamino', false);
    const connectToCollege = get(this.props.hostContext, 'connectToCollege', false);
    const hasAdmissionsEmail = get(admissionsOffice, 'email.value', '') !== '';

    return (
      <div className="webLinks-container">
        {isCamino && connectToCollege && (
          <button
            aria-label="Connect"
            className="connect-btn"
            data-cy="weblinks-connect-btn"
            onClick={this.handleConnectClick}
          >
            <FormattedMessage {...messages.connect} />
            <Icon name="linkify" size="small" />
          </button>
        )}
        {virtualTour && (
          <a
            data-cy="weblinks-virtual-tour-link"
            aria-label="Virtual tour link"
            className="apply-online-link"
            href={getUrlWithProtocol(virtualTour)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={this.handleVirtualTourClick}
          >
            <FormattedMessage {...messages.virtualTour} />
          </a>
        )}
        {!isCamino && (
          <div data-cy={'weblinks-communicate'} className="communicate-container">
            <CommunicateLink
              premium_links={hubsWebLinks.premium_links}
              collegeContact={collegeContact}
              collegeName={collegeName}
              userEmail={userEmail}
              openAdmissionsModal={hasAdmissionsEmail ? this.openAdmissionsModal : null}
              sendEmail={sendEmail}
            />
          </div>
        )}
        {applyOnlineLink && (
          <a
            data-cy="weblinks-applyonline-link"
            aria-label="Apply online link"
            className="apply-online-link"
            href={getUrlWithProtocol(applyOnlineLink.url)}
            target="_blank"
            rel="noopener noreferrer"
            data-click-ppid={applyOnlineLink.product_id}
            data-click-location={trackingLocations.APPLY_ONLINE}
            data-click-label={trackingLabels.APPLY_ONLINE}
            data-click-category={trackingCategories.TOP_RIGHT_NAV}
            onClick={this.handleClick}
          >
            <FormattedMessage {...messages.applyOnline} />
          </a>
        )}
        <div data-cy={'weblinks-learnMore'} className="learnmore-container">
          <LearnMoreLink
            premium_links={hubsWebLinks.premium_links}
            collegeName={collegeName}
          />
        </div>
        {hasAdmissionsEmail && (
          <SendToAdmissions
            open={showAdmissionsModal}
            onClose={this.closeAdmissionsModal}
            userEmail={userEmail}
            admissionsOffice={admissionsOffice}
            sendEmail={sendEmail}
          />
        )}
      </div>
    );
  }
}

export default withHostContext(WebLinks);
